import { TipoEntidadeEnum } from '@core/models/entidade';

/**
 * Normaliza o payload de cadastro/edição de entidade antes do POST/PUT.
 * O select de entidade superior grava o id numérico; o de tipo de relação grava o objeto.
 */
export function extrairId(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') {
    return null;
  }
  if (typeof valor === 'object') {
    const id = (valor as { id?: number }).id;
    return id === undefined ? null : id;
  }
  const numerico = Number(valor);
  return Number.isNaN(numerico) ? null : numerico;
}

export function paraIsoOuNull(valor: unknown): string | null {
  if (valor === null || valor === undefined || valor === '') {
    return null;
  }
  const data = new Date(valor as string | number | Date);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}

/** Normaliza ids e datas do payload de entidade superior antes do POST/PUT na API. */
export function normalizarEntidadeSuperiorParaApi(entidadeSuperior: any): any {
  if (!entidadeSuperior || typeof entidadeSuperior !== 'object') {
    return entidadeSuperior;
  }
  const { dataCadastro, ...resto } = entidadeSuperior;
  return {
    ...resto,
    idEntidadeSuperior: extrairId(entidadeSuperior?.idEntidadeSuperior),
    tipoRelacaoId: extrairId(entidadeSuperior?.tipoRelacaoId),
    dataAssociacao: paraIsoOuNull(entidadeSuperior?.dataAssociacao),
    dataDesassociacao: paraIsoOuNull(entidadeSuperior?.dataDesassociacao),
  };
}

export function mapearEntidadeSuperioresParaApi(entidadeSuperiores: any[] | null | undefined): any[] {
  if (!entidadeSuperiores?.length) {
    return [];
  }
  return entidadeSuperiores.map((entidadeSuperior) => normalizarEntidadeSuperiorParaApi(entidadeSuperior));
}

export function superioresSemId(entidadeSuperiores: any[] | null | undefined): boolean {
  return (entidadeSuperiores ?? []).some((item) => extrairId(item?.idEntidadeSuperior) == null);
}

export function normalizarEntidadeContatosParaApi(contatos: unknown, outrosContatos?: unknown): any[] {
  return [...paraListaDeObjetos(contatos), ...paraListaDeObjetos(outrosContatos)];
}

const TIPOS_ENTIDADE_SEM_SECAO_CONTATO = [
  TipoEntidadeEnum.Orgao,
  TipoEntidadeEnum.Comissao,
  TipoEntidadeEnum.GrupoTrabalho,
  TipoEntidadeEnum.Subcomissao,
];

/** Área e demais tipos exibem Contatos/Outros Contatos no cadastro e na edição. */
export function tipoEntidadeExibeSecaoContatos(tipoEntidadeId: unknown): boolean {
  const id = extrairId(tipoEntidadeId);
  if (id == null) {
    return true;
  }
  if (id === TipoEntidadeEnum.Area) {
    return true;
  }
  return !TIPOS_ENTIDADE_SEM_SECAO_CONTATO.some((tipo) => tipo === id);
}

export function contatoPrincipalDaLista(contatos: unknown): any | null {
  const lista = paraListaDeObjetos(contatos);
  return lista.find((contato) => contato?.flagPrincipal) ?? lista[0] ?? null;
}

function paraListaDeObjetos(valor: unknown): any[] {
  if (valor == null || valor === '') {
    return [];
  }
  const lista = Array.isArray(valor) ? valor : [valor];
  return lista.filter((item) => item && typeof item === 'object');
}

export function snapshotEntidadeSuperior(lista: any[] | null | undefined, idOuObjeto: unknown): {
  id: number;
  nome: string;
  nomeReduzido: string;
  cnpj: string | null;
} | null {
  const id = extrairId(idOuObjeto);
  if (id == null) {
    return null;
  }
  const selecionada = (lista ?? []).find((entidade: any) => extrairId(entidade?.id) === id);
  if (selecionada) {
    const nome = selecionada.nome || selecionada.nomeRazaoSocial || '';
    return {
      id: selecionada.id,
      nome,
      nomeReduzido: selecionada.nomeFantasia || selecionada.nomeReduzido || nome,
      cnpj: selecionada.cnpj ?? null,
    };
  }
  if (typeof idOuObjeto === 'object' && idOuObjeto) {
    const obj = idOuObjeto as { id?: number; nome?: string; nomeReduzido?: string; cnpj?: string };
    return {
      id,
      nome: obj.nome || '',
      nomeReduzido: obj.nomeReduzido || obj.nome || '',
      cnpj: obj.cnpj ?? null,
    };
  }
  return { id, nome: '', nomeReduzido: '', cnpj: null };
}

export function nomeReduzidoSuperiorLinha(item: any): string {
  return item?.nomeReduzido
    || item?.idEntidadeSuperior?.nomeReduzido
    || '';
}

export function nomeSuperiorLinha(item: any): string {
  return item?.nome
    || item?.idEntidadeSuperior?.nome
    || '';
}

export function cnpjSuperiorLinha(item: any): string {
  return item?.cnpj
    || item?.idEntidadeSuperior?.cnpj
    || '';
}

export function nomeTipoRelacaoLinha(item: any): string {
  return item?.tipoRelacao?.nomeTipoRelacao
    || item?.tipoRelacaoId?.nomeTipoRelacao
    || '';
}

